import { useMutation } from '@tanstack/react-query';
import { Check, Copy, LocateFixed } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChurchLogoUploader } from '@/components/shared/church-logo-uploader';
import { CountrySelect } from '@/components/shared/country-select';
import { CurrencySelect } from '@/components/shared/currency-select';
import { FormField } from '@/components/shared/form-field';
import {
  LocationPicker,
  type ChurchLocation,
} from '@/components/shared/location-picker';
import { PageHeader } from '@/components/shared/page-header';
import { TimezoneSelect } from '@/components/shared/timezone-select';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ColorPicker } from '@/components/ui/color-picker';
import { FormFieldError } from '@/components/ui/form-field-error';
import { Input } from '@/components/ui/input';
import { Listbox } from '@/components/ui/listbox';
import { PhoneInput } from '@/components/ui/phone-input';
import { SwitchField } from '@/components/ui/switch';
import { toast } from '@/components/ui/toast';
import type { CountryCode } from '@/constants/countries';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { applyChurchBrand } from '@/lib/brand-colors';
import { uploadBlob } from '@/lib/upload';
import { useChurchStore, type PublicChurch } from '@/stores/church-store';
import { useTheme } from '@/stores/theme-store';
import { cn } from '@/utils/cn';

const LOCALES = [{ value: 'en', label: 'English' }];

/** Common check-in distances; server allows 20–5000 metres. */
const GPS_CHECK_IN_RADIUS_PRESETS = [50, 100, 150, 250, 500] as const;

export const ChurchSettingsPage = () => {
  const { t } = useTranslation('churches');
  const { t: tCommon } = useTranslation('common');
  const { t: tErrors } = useTranslation('errors');
  const church = useChurchStore((state) => state.church);
  const refreshCurrent = useChurchStore((state) => state.refreshCurrent);
  const restore = useChurchStore((state) => state.restore);
  const resolvedTheme = useTheme((state) => state.resolved);

  const isCampus = Boolean(church?.parentChurchId);
  const [name, setName] = useState(church?.name ?? '');
  const [campusName, setCampusName] = useState(church?.campusName ?? '');
  const [brandPrimary, setBrandPrimary] = useState(church?.brandPrimary ?? '');
  const [brandAccent, setBrandAccent] = useState(church?.brandAccent ?? '');
  const [country, setCountry] = useState<CountryCode | null>(
    (church?.country as CountryCode | undefined) ?? null,
  );
  const [timezone, setTimezone] = useState<string | null>(church?.defaultTimezone ?? null);
  const [currency, setCurrency] = useState<string | null>(church?.baseCurrency ?? null);
  const [locale, setLocale] = useState<string | null>(church?.defaultLocale ?? 'en');
  const [contactEmail, setContactEmail] = useState(church?.contactEmail ?? '');
  const [contactPhone, setContactPhone] = useState<string | null>(church?.contactPhone ?? null);
  const [contactAlternatePhone, setContactAlternatePhone] = useState<string | null>(
    church?.contactAlternatePhone ?? null,
  );
  const [location, setLocation] = useState<ChurchLocation | null>(() => {
    if (church?.locationLatitude != null && church?.locationLongitude != null) {
      return {
        latitude: church.locationLatitude,
        longitude: church.locationLongitude,
        address: church.address,
      };
    }
    return null;
  });
  const [gpsCheckInRadiusMetres, setGpsCheckInRadiusMetres] = useState(
    String(church?.gpsCheckInRadiusMetres ?? 150),
  );
  const [isAppSearchable, setIsAppSearchable] = useState(church?.isAppSearchable ?? false);
  const [showInNearby, setShowInNearby] = useState(church?.showInNearby ?? false);
  const [error, setError] = useState<string | null>(null);
  const [churchCodeCopied, setChurchCodeCopied] = useState(false);
  const [membershipNumberMode, setMembershipNumberMode] = useState<
    'manual' | 'auto' | 'auto_with_override'
  >(church?.membershipNumberMode ?? 'manual');
  const [membershipNumberPrefix, setMembershipNumberPrefix] = useState(
    church?.membershipNumberPrefix ?? '',
  );
  const [membershipNumberPadding, setMembershipNumberPadding] = useState(
    String(church?.membershipNumberPadding ?? 4),
  );
  const [membershipNumberNextValue, setMembershipNumberNextValue] = useState(
    String(church?.membershipNumberNextValue ?? 1),
  );

  const membershipNumberPreview = `${membershipNumberPrefix}${String(
    Number(membershipNumberNextValue) || 1,
  ).padStart(Number(membershipNumberPadding) || 4, '0')}`;

  const parsedGpsCheckInRadiusMetres = Math.min(
    5000,
    Math.max(20, Number(gpsCheckInRadiusMetres) || 150),
  );

  const save = useMutation({
    mutationFn: () =>
      api.patch<PublicChurch>('/churches/current', {
        ...(isCampus ? {} : { name }),
        campusName,
        country,
        defaultTimezone: timezone,
        baseCurrency: currency,
        defaultLocale: locale,
        contactEmail: contactEmail.trim() || null,
        contactPhone,
        contactAlternatePhone,
        address: location ? location.address?.trim() || null : null,
        locationLatitude: location?.latitude ?? null,
        locationLongitude: location?.longitude ?? null,
        gpsCheckInRadiusMetres: parsedGpsCheckInRadiusMetres,
        isAppSearchable,
        showInNearby: isAppSearchable ? showInNearby : false,
        ...(!isCampus ? { brandPrimary, brandAccent } : {}),
        membershipNumberMode,
        membershipNumberPrefix: membershipNumberPrefix.trim() || null,
        membershipNumberPadding: Number(membershipNumberPadding),
        membershipNumberNextValue: Number(membershipNumberNextValue),
      }),
    onSuccess: async () => {
      toast.success(t('settings.saved'));
      await refreshCurrent();
      await restore();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        setError(tErrors(err.code, err.params));
        return;
      }
      setError(tErrors('UNEXPECTED'));
    },
  });

  const saveLogo = async (logoKey: string | null) => {
    try {
      await api.patch<PublicChurch>('/churches/current', { logoKey });
      toast.success(t('settings.logoSaved'));
      await refreshCurrent();
      await restore();
    } catch (err) {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
      throw err;
    }
  };

  if (!church) return null;

  return (
    <div className="space-y-6">
      <PageHeader title={t('settings.title')} description={t('settings.description')} />

      <form
        className="flex flex-col gap-6"
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          save.mutate();
        }}
      >
        <div className="grid gap-6 pb-24 lg:grid-cols-2 lg:items-start">
          <div className="flex min-w-0 flex-col gap-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('settings.sections.profile.title')}</CardTitle>
                <CardDescription>{t('settings.sections.profile.description')}</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-6">
                <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
                  <ChurchLogoUploader
                    layout="stacked"
                    src={church.logoUrl}
                    name={church.name}
                    className="w-36 max-w-none shrink-0"
                    onUpload={async (blob) => {
                      const { key } = await uploadBlob({ purpose: 'church_logo', blob });
                      await saveLogo(key);
                    }}
                    onRemove={() => void saveLogo(null)}
                    labels={{
                      change: t('settings.logoChange'),
                      remove: t('settings.logoRemove'),
                      cropTitle: t('settings.logoCropTitle'),
                      zoom: tCommon('media.zoom'),
                      apply: t('settings.logoApply'),
                      cancel: tCommon('action.cancel'),
                      instructions: t('settings.logoInstructions'),
                      closeCropper: tCommon('action.close'),
                    }}
                  />

                  <div className="flex min-w-0 flex-1 flex-col gap-4">
                    {!isCampus ? (
                      <FormField label={t('fields.name')}>
                        {(field) => (
                          <Input
                            id={field.id}
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            required
                            aria-describedby={field['aria-describedby']}
                            aria-invalid={field.invalid}
                          />
                        )}
                      </FormField>
                    ) : null}

                    <FormField label={t('fields.campusName')}>
                      {(field) => (
                        <Input
                          id={field.id}
                          value={campusName}
                          onChange={(event) => setCampusName(event.target.value)}
                          required
                          aria-describedby={field['aria-describedby']}
                          aria-invalid={field.invalid}
                        />
                      )}
                    </FormField>

                    <FormField label={t('fields.churchCode')} hint={t('settings.churchCodeHint')}>
                      {(field) => (
                        <div className="flex gap-2">
                          <Input
                            id={field.id}
                            readOnly
                            value={church.slug}
                            onFocus={(event) => event.target.select()}
                            aria-describedby={field['aria-describedby']}
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={async () => {
                              await navigator.clipboard.writeText(church.slug);
                              setChurchCodeCopied(true);
                              setTimeout(() => setChurchCodeCopied(false), 2000);
                            }}
                            leadingIcon={
                              churchCodeCopied ? (
                                <Check className="h-4 w-4" />
                              ) : (
                                <Copy className="h-4 w-4" />
                              )
                            }
                          >
                            {churchCodeCopied ? tCommon('action.copied') : tCommon('action.copy')}
                          </Button>
                        </div>
                      )}
                    </FormField>
                  </div>
                </div>

                {!isCampus ? (
                  <div className="grid gap-4 border-t border-border pt-6 sm:grid-cols-2">
                    <FormField label={t('fields.brandPrimary')} hint={t('fields.brandPrimaryHint')}>
                      {(field) => (
                        <ColorPicker
                          id={field.id}
                          pickerLabel={t('fields.brandPrimary')}
                          value={brandPrimary}
                          onChange={(value) => {
                            setBrandPrimary(value);
                            applyChurchBrand(
                              { brandPrimary: value, brandAccent },
                              resolvedTheme,
                            );
                          }}
                          placeholder={t('fields.brandPrimaryPlaceholder')}
                          aria-describedby={field['aria-describedby']}
                          aria-invalid={field.invalid}
                        />
                      )}
                    </FormField>

                    <FormField label={t('fields.brandAccent')} hint={t('fields.brandAccentHint')}>
                      {(field) => (
                        <ColorPicker
                          id={field.id}
                          pickerLabel={t('fields.brandAccent')}
                          value={brandAccent}
                          onChange={(value) => {
                            setBrandAccent(value);
                            applyChurchBrand(
                              { brandPrimary, brandAccent: value },
                              resolvedTheme,
                            );
                          }}
                          placeholder={t('fields.brandAccentPlaceholder')}
                          aria-describedby={field['aria-describedby']}
                          aria-invalid={field.invalid}
                        />
                      )}
                    </FormField>
                  </div>
                ) : null}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('settings.sections.contact.title')}</CardTitle>
                <CardDescription>{t('settings.sections.contact.description')}</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <FormField label={t('fields.contactEmail')}>
                  {(field) => (
                    <Input
                      id={field.id}
                      type="email"
                      value={contactEmail}
                      onChange={(event) => setContactEmail(event.target.value)}
                      placeholder={t('fields.contactEmailPlaceholder')}
                      aria-describedby={field['aria-describedby']}
                      aria-invalid={field.invalid}
                    />
                  )}
                </FormField>

                <FormField label={t('fields.contactPhone')}>
                  {(field) => (
                    <PhoneInput
                      id={field.id}
                      value={contactPhone}
                      onChange={setContactPhone}
                      aria-describedby={field['aria-describedby']}
                      invalid={field.invalid}
                    />
                  )}
                </FormField>

                <FormField label={t('fields.contactAlternatePhone')}>
                  {(field) => (
                    <PhoneInput
                      id={field.id}
                      value={contactAlternatePhone}
                      onChange={setContactAlternatePhone}
                      aria-describedby={field['aria-describedby']}
                      invalid={field.invalid}
                    />
                  )}
                </FormField>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('settings.sections.regional.title')}</CardTitle>
                <CardDescription>{t('settings.sections.regional.description')}</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 sm:grid-cols-2">
                <FormField label={t('fields.country')}>
                  {(field) => (
                    <CountrySelect
                      id={field.id}
                      value={country}
                      onChange={setCountry}
                      aria-describedby={field['aria-describedby']}
                      invalid={field.invalid}
                    />
                  )}
                </FormField>

                <FormField label={t('fields.timezone')} tipId="settings.church.timezone">
                  {(field) => (
                    <TimezoneSelect
                      id={field.id}
                      value={timezone}
                      onChange={setTimezone}
                      aria-describedby={field['aria-describedby']}
                      invalid={field.invalid}
                    />
                  )}
                </FormField>

                <FormField label={t('fields.currency')} tipId="settings.church.baseCurrency">
                  {(field) => (
                    <CurrencySelect
                      id={field.id}
                      value={currency}
                      onChange={setCurrency}
                      currencies={church.supportedCurrencies}
                      aria-describedby={field['aria-describedby']}
                      invalid={field.invalid}
                    />
                  )}
                </FormField>

                <FormField label={t('fields.locale')} tipId="settings.church.locale">
                  {(field) => (
                    <Listbox
                      id={field.id}
                      value={locale}
                      onChange={setLocale}
                      options={LOCALES}
                      aria-describedby={field['aria-describedby']}
                      invalid={field.invalid}
                    />
                  )}
                </FormField>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('settings.sections.membershipNumbers.title')}</CardTitle>
                <CardDescription>
                  {t('settings.sections.membershipNumbers.description')}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-4">
                <FormField label={t('settings.sections.membershipNumbers.mode')}>
                  {(field) => (
                    <Listbox
                      id={field.id}
                      value={membershipNumberMode}
                      onChange={(next) =>
                        setMembershipNumberMode(
                          (next as 'manual' | 'auto' | 'auto_with_override' | null) ?? 'manual',
                        )
                      }
                      options={[
                        {
                          value: 'manual',
                          label: t('settings.sections.membershipNumbers.modeManual'),
                        },
                        {
                          value: 'auto',
                          label: t('settings.sections.membershipNumbers.modeAuto'),
                        },
                        {
                          value: 'auto_with_override',
                          label: t('settings.sections.membershipNumbers.modeAutoWithOverride'),
                        },
                      ]}
                      aria-describedby={field['aria-describedby']}
                      invalid={field.invalid}
                    />
                  )}
                </FormField>

                <div className="grid gap-4 sm:grid-cols-3">
                  <FormField label={t('settings.sections.membershipNumbers.prefix')}>
                    {(field) => (
                      <Input
                        id={field.id}
                        value={membershipNumberPrefix}
                        onChange={(event) => setMembershipNumberPrefix(event.target.value)}
                        aria-describedby={field['aria-describedby']}
                        aria-invalid={field.invalid}
                      />
                    )}
                  </FormField>

                  <FormField label={t('settings.sections.membershipNumbers.padding')}>
                    {(field) => (
                      <Input
                        id={field.id}
                        type="number"
                        min={1}
                        max={10}
                        value={membershipNumberPadding}
                        onChange={(event) => setMembershipNumberPadding(event.target.value)}
                        aria-describedby={field['aria-describedby']}
                        aria-invalid={field.invalid}
                      />
                    )}
                  </FormField>

                  <FormField label={t('settings.sections.membershipNumbers.nextValue')}>
                    {(field) => (
                      <Input
                        id={field.id}
                        type="number"
                        min={1}
                        value={membershipNumberNextValue}
                        onChange={(event) => setMembershipNumberNextValue(event.target.value)}
                        aria-describedby={field['aria-describedby']}
                        aria-invalid={field.invalid}
                      />
                    )}
                  </FormField>
                </div>

                <p className="text-body-sm text-fg-secondary">
                  {t('settings.sections.membershipNumbers.preview', {
                    value: membershipNumberPreview,
                  })}
                </p>
                <p className="text-caption text-fg-secondary">
                  {membershipNumberMode === 'manual'
                    ? t('settings.sections.membershipNumbers.modeManualHelp')
                    : membershipNumberMode === 'auto'
                      ? t('settings.sections.membershipNumbers.modeAutoHelp')
                      : t('settings.sections.membershipNumbers.modeAutoWithOverrideHelp')}
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="flex min-w-0 flex-col gap-6 lg:sticky lg:top-6">
            <Card>
              <CardHeader>
                <CardTitle>{t('settings.sections.appDiscovery.title')}</CardTitle>
                <CardDescription>{t('settings.sections.appDiscovery.description')}</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-6">
                <SwitchField
                  id="church-app-searchable"
                  label={t('settings.sections.appDiscovery.makePublic.label')}
                  description={t('settings.sections.appDiscovery.makePublic.description')}
                  checked={isAppSearchable}
                  onCheckedChange={(checked) => {
                    setIsAppSearchable(checked);
                    if (!checked) setShowInNearby(false);
                  }}
                  disabled={save.isPending}
                />

                <div className="rounded-lg border border-border p-4">
                  <SwitchField
                    id="church-show-in-nearby"
                    label={t('settings.sections.appDiscovery.showInNearby.label')}
                    description={t('settings.sections.appDiscovery.showInNearby.description')}
                    checked={showInNearby}
                    onCheckedChange={setShowInNearby}
                    disabled={!isAppSearchable || save.isPending}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('settings.sections.location.title')}</CardTitle>
                <CardDescription>{t('settings.sections.location.description')}</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col gap-6">
                <LocationPicker
                  value={location}
                  onChange={setLocation}
                  country={country}
                  radiusMetres={parsedGpsCheckInRadiusMetres}
                  mapClassName="h-80 min-h-80"
                />

                <div className="border-t border-border pt-6">
                  <div className="overflow-hidden rounded-lg border border-border bg-surface-sunken">
                    <div className="flex flex-col gap-6 p-5">
                      <div className="flex gap-4">
                        <div className="flex size-11 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-brand shadow-e1">
                          <LocateFixed className="size-5" aria-hidden />
                        </div>
                        <div className="min-w-0 space-y-1.5">
                          <h3 className="text-body font-medium text-fg">
                            {t('settings.sections.location.gpsRadiusSectionTitle')}
                          </h3>
                          <p className="text-body-sm text-fg-secondary">
                            {t('settings.sections.location.gpsRadiusSectionDescription')}
                          </p>
                        </div>
                      </div>

                      <FormField
                        label={t('settings.sections.location.gpsRadius')}
                        hint={t('settings.sections.location.gpsRadiusHint')}
                      >
                        {(field) => (
                          <div className="space-y-3">
                            <Input
                              id={field.id}
                              type="text"
                              inputMode="numeric"
                              value={gpsCheckInRadiusMetres}
                              onChange={(event) => setGpsCheckInRadiusMetres(event.target.value)}
                              className="tabular-nums"
                              suffix={
                                <span className="text-body-sm text-fg-secondary">
                                  {t('settings.sections.location.gpsRadiusUnit')}
                                </span>
                              }
                              aria-describedby={field['aria-describedby']}
                              aria-invalid={field.invalid}
                            />
                            <div
                              className="flex flex-wrap gap-2"
                              role="group"
                              aria-label={t('settings.sections.location.gpsRadiusPresets')}
                            >
                              {GPS_CHECK_IN_RADIUS_PRESETS.map((preset) => {
                                const selected = parsedGpsCheckInRadiusMetres === preset;
                                return (
                                  <Button
                                    key={preset}
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className={cn(
                                      'min-w-[3.5rem] tabular-nums',
                                      selected &&
                                        'border-brand bg-brand/10 text-brand hover:border-brand hover:bg-brand/15',
                                    )}
                                    aria-pressed={selected}
                                    onClick={() => setGpsCheckInRadiusMetres(String(preset))}
                                  >
                                    {t('settings.sections.location.gpsRadiusPreset', {
                                      value: preset,
                                    })}
                                  </Button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </FormField>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-surface/95 backdrop-blur-sm md:left-60">
          <div className="mx-auto flex max-w-[var(--content-max-width)] items-center gap-4 px-4 py-3 sm:px-6">
            <FormFieldError message={error ?? undefined} className="min-w-0 flex-1" />
            <div className="ml-auto flex shrink-0 justify-end gap-2">
              <Button type="submit" loading={save.isPending}>
                {t('settings.save')}
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
